import { useState, useEffect } from 'react';
import {
  CaseReport,
  AlignmentMetric,
  MetricComparison,
  MetricSession,
} from '../../models/CaseTypes';
import AchievedCorrectionViewModel from '../../models/AchievedCorrectionViewModel';
import { logTrace } from '../../utils/trackingUtils';

const ALL_VERT_INDICATOR = 'All';

interface UseAchievedCorrectionReturn {
  loading: boolean;
  error: string;
  metricSessions: MetricSession[];
  primarySession: MetricSession | null;
  comparisonSession: MetricSession | null;
  primaryMeshSignedUrl: string | null;
  comparisonMeshSignedUrl: string | null;
  selectedVertebraChecks: Record<string, boolean>;
  selectedVertebraeNames: string[];
  selectedPlaneNames: string[];
  preOpData: AlignmentMetric[];
  comparisonData: MetricComparison[];
  validationSignedUrl: string | null;
  handlePrimaryChange: (selectedValue: string) => void;
  handleComparisonChange: (selectedValue: string) => void;
  handleVertebraCheckboxChange: (comparison: MetricComparison, checked: boolean) => void;
}

export const useAchievedCorrection = (caseReport: CaseReport): UseAchievedCorrectionReturn => {
  const [metricSessions, setMetricSessions] = useState<MetricSession[]>([]);
  const [primarySession, setPrimarySession] = useState<MetricSession | null>(null);
  const [comparisonSession, setComparisonSession] = useState<MetricSession | null>(null);
  const [primaryMeshSignedUrl, setPrimaryMeshSignedUrl] = useState<string | null>(null);
  const [comparisonMeshSignedUrl, setComparisonMeshSignedUrl] = useState<string | null>(null);
  const [selectedVertebraChecks, setSelectedVertebraChecks] = useState<Record<string, boolean>>({});
  const [selectedVertebraeNames, setSelectedVertebraeNames] = useState<string[]>([ALL_VERT_INDICATOR]);
  const [selectedPlaneNames, setSelectedPlaneNames] = useState<string[]>([]);
  const [preOpData, setPreOpData] = useState<AlignmentMetric[]>([]);
  const [comparisonData, setComparisonData] = useState<MetricComparison[]>([]);
  const [validationSignedUrl, setValidationSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [viewModel, setViewModel] = useState<AchievedCorrectionViewModel | null>(null);

  useEffect(() => {
    if (!caseReport) {
      return;
    }
    const thisViewModel = new AchievedCorrectionViewModel(caseReport);
    setViewModel(thisViewModel);

    const fetchCorrectionData = async (theViewModel: AchievedCorrectionViewModel): Promise<void> => {
      try {
        setLoading(true);
        setMetricSessions(theViewModel.getMetricSessions());
        setValidationSignedUrl(theViewModel.getPostOpValidationSignedUrl());

        const defaultPrimary = theViewModel.getDefaultPrimarySession();
        setPrimarySession(defaultPrimary);
        if (defaultPrimary?.meshAssetSignedUrl) {
          setPrimaryMeshSignedUrl(defaultPrimary.meshAssetSignedUrl);
        }

        const defaultSecondary = theViewModel.getDefaultSessionForComparison();
        setComparisonSession(defaultSecondary);
        if (defaultSecondary?.meshAssetSignedUrl) {
          setComparisonMeshSignedUrl(defaultSecondary.meshAssetSignedUrl);
        }

        setPreOpData(theViewModel.getPreOpData());
      } catch (err: unknown) {
        console.error('Error setting up achieved correction view:', err);
        setError('Could not load achieved correction data: ' + (err as Error).message);
      } finally {
        setLoading(false);
      }
    };

    fetchCorrectionData(thisViewModel);
  }, [caseReport]);

  useEffect(() => {
    if (!caseReport || viewModel === null || comparisonSession === null || primarySession === null) {
      return;
    }
    setComparisonData(
      viewModel.getComparisonData(
        primarySession.sessionName,
        comparisonSession.sessionName
      )
    );
  }, [comparisonSession, caseReport, primarySession, viewModel]);

  const handleComparisonChange = (selectedValue: string): void => {
    logTrace('AchievedCorrection', 'proprio_change_comparison_3d_view_' + selectedValue, selectedValue);
    const session = viewModel?.getMetricSession(selectedValue);
    if (session) {
      setComparisonSession(session);
      if (session.meshAssetSignedUrl) {
        setComparisonMeshSignedUrl(session.meshAssetSignedUrl);
      }
    }
  };

  const handlePrimaryChange = (selectedValue: string): void => {
    logTrace('AchievedCorrection', 'proprio_change_primary_3d_view_' + selectedValue, selectedValue);
    const session = viewModel?.getMetricSession(selectedValue);
    if (session) {
      setPrimarySession(session);
      if (session.meshAssetSignedUrl) {
        setPrimaryMeshSignedUrl(session.meshAssetSignedUrl);
      }
    }
  };

  const handleVertebraCheckboxChange = (comparison: MetricComparison, checked: boolean): void => {
    if (!viewModel) return;

    const updatedChecks = { ...selectedVertebraChecks, [comparison.vertebra]: checked };
    setSelectedVertebraChecks(updatedChecks);

    const selectedVertebrae = Object.keys(updatedChecks).filter(key => updatedChecks[key]);
    const distinctVertebrae = new Set<string>();
    selectedVertebrae.forEach(v => {
      viewModel.getVertebraEdgeNodesFromRange(v).forEach(individual => distinctVertebrae.add(individual));
    });

    const selectedVerts = distinctVertebrae.size === 0 ? [ALL_VERT_INDICATOR] : Array.from(distinctVertebrae);
    setSelectedVertebraeNames(selectedVerts);

    const levelMetrics = comparisonData.filter(m => m.vertebra === comparison.vertebra);
    const allLevelPlanes: Set<string> = new Set<string>();
    for (const levelMetric of levelMetrics) {
      const planes = viewModel.getVertebralPlanes(levelMetric);
      planes.forEach(plane => allLevelPlanes.add(plane));
    }

    setSelectedPlaneNames(prev => {
      const updated = new Set<string>(prev);
      allLevelPlanes.forEach(plane => {
        if (checked) {
          updated.add(plane);
        } else {
          updated.delete(plane);
        }
      });
      return Array.from(updated);
    });
  };

  return {
    loading,
    error,
    metricSessions,
    primarySession,
    comparisonSession,
    primaryMeshSignedUrl,
    comparisonMeshSignedUrl,
    selectedVertebraChecks,
    selectedVertebraeNames,
    selectedPlaneNames,
    preOpData,
    comparisonData,
    validationSignedUrl,
    handlePrimaryChange,
    handleComparisonChange,
    handleVertebraCheckboxChange,
  };
};
